import {noop} from 'es-toolkit'
import {useFormik} from 'formik'
import * as Yup from 'yup'

import {formatDeliveryTime} from '../../libs/formatter'
import {calculateProductionTimes} from '../../libs/production-time'
import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'

import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Accordion from '../../components/accordion'
import AccordionItem from '../../components/accordion/AccordionItem'
import ContentBox from '../../components/content-box'
import LabeledPrice from '../../components/labeled-price'

type Props = {
  dynamicProductionTimeRequest?: Partial<dynamicProductionTimeApi.DynamicProductionTimeRequest>
}

type FormData = {
  largestAxisSize: string
  defaultProductionTimeMin: string
  defaultProductionTimeMax: string
  quantity: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const validationSchema = Yup.object().shape({
  largestAxisSize: Yup.number().min(0).required(),
  defaultProductionTimeMin: Yup.number().min(0).required(),
  defaultProductionTimeMax: Yup.number().min(0).required(),
  quantity: Yup.number().min(1).required(),
})

const initialValues = {
  largestAxisSize: '',
  defaultProductionTimeMin: '',
  defaultProductionTimeMax: '',
  quantity: '1',
}

const ProductionTimeCalculatorPartial = ({dynamicProductionTimeRequest}: Props) => {
  const formik = useFormik<FormData>({
    initialValues,
    initialErrors: {formikGeneral: 'initial error'},
    validationSchema,
    onSubmit: noop,
  })

  let calculatedProductionTimes: ReturnType<typeof calculateProductionTimes> | null = null
  if (formik.isValid && dynamicProductionTimeRequest) {
    calculatedProductionTimes = calculateProductionTimes(dynamicProductionTimeRequest, {
      largestAxisSize: formik.values.largestAxisSize
        ? parseFloat(formik.values.largestAxisSize)
        : undefined,
      defaultProductionTimeMin: formik.values.defaultProductionTimeMin
        ? parseFloat(formik.values.defaultProductionTimeMin)
        : undefined,
      defaultProductionTimeMax: formik.values.defaultProductionTimeMax
        ? parseFloat(formik.values.defaultProductionTimeMax)
        : undefined,
      quantity: formik.values.quantity ? parseFloat(formik.values.quantity) : undefined,
    })
  }

  return (
    <>
      <ContentBox>
        <Paragraph size="s" minor>
          You can use our example calculator to find out how your production time would be adapted
          for certain model dimensions on Craftcloud.
        </Paragraph>
        <Headline size="s" label="Example model parameters" className="u-margin-bottom-xs" />
        <FormRow layout="half-half">
          <NumberInputField
            allowDecimals
            name="largestAxisSize"
            placeholder="Largest axis size"
            value={formik.values.largestAxisSize}
            suffix="mm"
            appearance={
              formik.values.largestAxisSize !== initialValues.largestAxisSize &&
              !!formik.errors.largestAxisSize
                ? 'error'
                : undefined
            }
            onChange={(value, name) => formik.setFieldValue(name as string, value)}
            onBlur={formik.handleBlur}
          />
          <NumberInputField
            name="quantity"
            placeholder="Quantity"
            value={formik.values.quantity}
            suffix="pieces"
            appearance={
              formik.values.quantity !== initialValues.quantity && !!formik.errors.quantity
                ? 'error'
                : undefined
            }
            onChange={(value, name) => formik.setFieldValue(name as string, value)}
            onBlur={formik.handleBlur}
          />
        </FormRow>
        <Headline size="s" label="Default production time" className="u-margin-bottom-xs" />
        <FormRow layout="half-half" divider="-">
          <NumberInputField
            name="defaultProductionTimeMin"
            placeholder="Min"
            value={formik.values.defaultProductionTimeMin}
            suffix={`business day${formik.values.defaultProductionTimeMin !== '1' ? 's' : ''}`}
            appearance={
              formik.submitCount > 0 && !!formik.errors.defaultProductionTimeMin
                ? 'error'
                : undefined
            }
            onChange={(value, name) => formik.setFieldValue(name as string, value)}
            onBlur={formik.handleBlur}
          />
          <NumberInputField
            name="defaultProductionTimeMax"
            placeholder="Max"
            value={formik.values.defaultProductionTimeMax}
            suffix={`business day${formik.values.defaultProductionTimeMax !== '1' ? 's' : ''}`}
            appearance={
              formik.submitCount > 0 && !!formik.errors.defaultProductionTimeMax
                ? 'error'
                : undefined
            }
            onChange={(value, name) => formik.setFieldValue(name as string, value)}
            onBlur={formik.handleBlur}
          />
        </FormRow>
        <LabeledPrice label="Additional production time">
          {calculatedProductionTimes ? calculatedProductionTimes.resultStepsOnTop : '-'}
        </LabeledPrice>
        <LabeledPrice label="Total production time" strong>
          {calculatedProductionTimes
            ? formatDeliveryTime(
                `${calculatedProductionTimes.resultProductionTimeMin} - ${calculatedProductionTimes.resultProductionTimeMax}`,
                'business day',
                'business days'
              )
            : '-'}
        </LabeledPrice>
      </ContentBox>
      <Accordion className="u-margin-top-xs">
        <AccordionItem title="Production time calculation details">
          <Paragraph size="s">
            <strong>Applicable quantity threshold</strong>
            <br />
            Find quantity threshold by matching the given model axis size to the nearest predefined
            axis size that is equal to or larger than it
          </Paragraph>
          <Paragraph size="s">
            <strong>Increase steps multiplier</strong>
            <br />(<em>quantity</em> / <em>applicable quantity threshold</em>) or 0
          </Paragraph>
          <Paragraph size="s">
            <strong>Calculated additional production time</strong>
            <br />
            <em>increase steps</em> * <em>increase steps multiplier</em>
          </Paragraph>
          <Paragraph size="s">
            <strong>Additional production time</strong>
            <br />
            Minimum of (<em>calculated additional production time</em>, <em>max increase</em>)
          </Paragraph>
          <Paragraph size="s">
            <strong>Total production time</strong>
            <br />(<em>default production time min</em> + <em>additional production time</em>) to (
            <em>default production time max</em> + <em>additional production time</em>) business
            days
          </Paragraph>
        </AccordionItem>
      </Accordion>
    </>
  )
}
export default ProductionTimeCalculatorPartial
